/**
 * F-17-marks: formatting applied to PART of a cell's text — bold, strike-
 * through, ink color and highlight over a character range, rather than over
 * the whole cell (which is what the F-17 `bold`/`color`/`bg` format props do).
 *
 * PURE logic — no Yjs, no DOM. The doc side (one flat CRDT key per mark kind,
 * C2) lives in ./doc/schema.ts + ./doc/board.ts; the renderers (grid cell,
 * script block mirror) consume `segmentMarkedText`.
 *
 * ## Why offsets + a quote, and not Y.Text attributes
 *
 * Character-range formatting really wants CRDT-native positions, and Yjs has
 * them — but only for a cell whose `v` is a `Y.Text`, which is the `longText`
 * column type alone (docs/07 C7). Marks have to work on every column, so they
 * take the anchoring model this codebase already sanctions for text ranges:
 * **offsets are a hint, the quoted text is the anchor** (`Anchor.sel`,
 * schema.ts — script-view comment highlights resolve exactly this way). A mark
 * whose text is gone resolves to nothing and simply stops rendering.
 *
 * Two things keep that honest:
 *
 *  - the quote is a bounded PREFIX (`TEXT_MARK_QUOTE_MAX`) plus the range's
 *    length, not the whole range. Bolding a paragraph must not copy the
 *    paragraph into the doc a second time, and — more importantly — a mark
 *    must survive an edit INSIDE its own range, which whole-quote
 *    verification could never do;
 *  - `rebaseTextMarks` shifts marks through the writer's OWN edit before it
 *    lands, so local typing keeps every range exact and the quote search is
 *    only ever a fallback for a peer's concurrent edit.
 *
 * ## Invariants
 *
 * Stored spans of ONE kind are sorted, non-overlapping and non-adjacent-with-
 * equal-color, because every writer goes through `applyMarkRange` /
 * `clearMarkRange`, which subtract before they insert. Readers still
 * normalize defensively: the doc is a shared mutable structure and a future
 * writer is not a proof.
 */
import { textSplice } from "./text-splice.js";
import { textTypeRuns, } from "./text-types.js";
export const TEXT_MARK_KINDS = ["bold", "strike", "color", "hl"];
/**
 * How much of a marked range is stored as its re-anchoring quote. Long enough
 * to be unambiguous in a paragraph, short enough that marking a whole script
 * block does not duplicate the block into the doc.
 */
export const TEXT_MARK_QUOTE_MAX = 64;
/* ------------------------------------------------------------------ */
/* Reading marks out of the doc                                        */
/* ------------------------------------------------------------------ */
/**
 * Coerce an untyped doc value into marks. Everything in the cells map is
 * `unknown` — written by this client, by a peer on an older build, or by a
 * server-side job — so a malformed entry must render as "no marks", never
 * throw inside a render pass.
 */
export function sanitizeTextMarks(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const entry of raw) {
        if (typeof entry !== "object" || entry === null)
            continue;
        const { start, end, quote, color } = entry;
        if (typeof start !== "number" || typeof end !== "number")
            continue;
        if (!Number.isInteger(start) || !Number.isInteger(end))
            continue;
        if (start < 0 || end <= start)
            continue;
        if (typeof quote !== "string" || quote.length === 0)
            continue;
        const mark = { start, end, quote };
        if (typeof color === "string" && color.length > 0)
            mark.color = color;
        out.push(mark);
    }
    return out;
}
/**
 * Where `mark` sits in `text` now, or null when its text is gone.
 *
 * The stored offsets win when the quote is still there (the common case: no
 * edit, or an edit this writer already rebased). Otherwise the NEAREST
 * occurrence of the quote to the stored start wins — nearest, not first,
 * because a script block says "the" a dozen times and a mark that jumped to
 * the top of the line on every edit would be worse than one that vanished.
 */
export function resolveTextMark(text, mark) {
    const length = mark.end - mark.start;
    if (length <= 0 || text.length === 0)
        return null;
    const span = (start) => {
        const end = Math.min(start + length, text.length);
        if (end <= start)
            return null;
        return mark.color === undefined ? { start, end } : { start, end, color: mark.color };
    };
    if (mark.start < text.length &&
        text.startsWith(mark.quote, mark.start) &&
        mark.start + length <= text.length) {
        return span(mark.start);
    }
    let best = null;
    for (let i = text.indexOf(mark.quote); i >= 0; i = text.indexOf(mark.quote, i + 1)) {
        if (best === null || Math.abs(i - mark.start) < Math.abs(best - mark.start))
            best = i;
    }
    return best === null ? null : span(best);
}
/**
 * Every mark of one kind, resolved against `text`: sorted, clipped to the
 * text, and de-overlapped (earlier wins) so downstream segmentation can walk
 * them in one pass.
 */
export function resolveTextMarks(text, marks) {
    const spans = [];
    for (const mark of marks) {
        const span = resolveTextMark(text, mark);
        if (span !== null)
            spans.push(span);
    }
    return normalizeSpans(spans);
}
/** Sort, drop overlaps (earlier wins), and merge touching spans of one color. */
function normalizeSpans(spans) {
    const sorted = [...spans].sort((a, b) => a.start - b.start || a.end - b.end);
    const out = [];
    for (const span of sorted) {
        const last = out[out.length - 1];
        if (last === undefined) {
            out.push({ ...span });
            continue;
        }
        if (span.start >= last.end) {
            if (span.start === last.end && span.color === last.color)
                last.end = span.end;
            else
                out.push({ ...span });
            continue;
        }
        // Overlap: the earlier span keeps what it has, the later one contributes
        // only its tail (dropped entirely when it adds nothing).
        if (span.end > last.end) {
            if (span.color === last.color)
                last.end = span.end;
            else
                out.push({ ...span, start: last.end });
        }
    }
    return out;
}
/* ------------------------------------------------------------------ */
/* Writing marks                                                       */
/* ------------------------------------------------------------------ */
/** Re-anchor resolved spans to `text` — the stored form of a write. */
function spansToMarks(text, spans) {
    const out = [];
    for (const span of spans) {
        const start = Math.max(0, Math.min(span.start, text.length));
        const end = Math.max(start, Math.min(span.end, text.length));
        if (end <= start)
            continue;
        const mark = {
            start,
            end,
            quote: text.slice(start, Math.min(end, start + TEXT_MARK_QUOTE_MAX)),
        };
        if (span.color !== undefined)
            mark.color = span.color;
        out.push(mark);
    }
    return out;
}
/** `spans` with `[start, end)` cut out of them (splitting where it lands inside). */
function subtractRange(spans, start, end) {
    const out = [];
    for (const span of spans) {
        if (span.end <= start || span.start >= end) {
            out.push(span);
            continue;
        }
        if (span.start < start)
            out.push({ ...span, end: start });
        if (span.end > end)
            out.push({ ...span, start: end });
    }
    return out;
}
/**
 * Mark `[start, end)` — the write behind "bold this", "highlight this yellow".
 * The range is subtracted from what is there before the new span goes in, so
 * re-highlighting an overlapping range in another color REPLACES the color
 * over the overlap instead of layering two answers on the same characters.
 *
 * Returns marks re-anchored to `text`, which is also what prunes marks whose
 * text has since been deleted: they resolve to nothing and never come back.
 */
export function applyMarkRange(text, marks, start, end, color) {
    const from = Math.max(0, Math.min(start, text.length));
    const to = Math.max(from, Math.min(end, text.length));
    if (to <= from)
        return spansToMarks(text, resolveTextMarks(text, marks));
    const kept = subtractRange(resolveTextMarks(text, marks), from, to);
    const span = color === undefined ? { start: from, end: to } : { start: from, end: to, color };
    return spansToMarks(text, normalizeSpans([...kept, span]));
}
/** Unmark `[start, end)` — "un-bold this", "remove the highlight here". */
export function clearMarkRange(text, marks, start, end) {
    const from = Math.max(0, Math.min(start, text.length));
    const to = Math.max(from, Math.min(end, text.length));
    const spans = resolveTextMarks(text, marks);
    return spansToMarks(text, to <= from ? spans : subtractRange(spans, from, to));
}
/**
 * Is EVERY character of `[start, end)` already marked? What the bold/strike
 * buttons toggle on: a selection that is entirely bold un-bolds, a partly
 * bold one bolds the rest — the rule every editor uses.
 */
export function rangeFullyMarked(text, marks, start, end) {
    const from = Math.max(0, Math.min(start, text.length));
    const to = Math.max(from, Math.min(end, text.length));
    if (to <= from)
        return false;
    let cursor = from;
    for (const span of resolveTextMarks(text, marks)) {
        if (span.end <= cursor)
            continue;
        if (span.start > cursor)
            return false;
        cursor = span.end;
        if (cursor >= to)
            return true;
    }
    return cursor >= to;
}
/**
 * The one color covering ALL of `[start, end)`, or undefined when the range is
 * uncolored or carries more than one color. Drives the swatch a color/highlight
 * button previews.
 */
export function rangeMarkColor(text, marks, start, end) {
    const from = Math.max(0, Math.min(start, text.length));
    const to = Math.max(from, Math.min(end, text.length));
    if (to <= from)
        return undefined;
    let cursor = from;
    let color;
    for (const span of resolveTextMarks(text, marks)) {
        if (span.end <= cursor)
            continue;
        if (span.start > cursor)
            return undefined;
        if (color === undefined)
            color = span.color;
        else if (color !== span.color)
            return undefined;
        cursor = span.end;
        if (cursor >= to)
            return color;
    }
    return cursor >= to ? color : undefined;
}
/* ------------------------------------------------------------------ */
/* Rebasing marks through the writer's own edit                        */
/* ------------------------------------------------------------------ */
/**
 * Shift `marks` from `before` to `after` — called on the way INTO the doc, so
 * a cell's marks describe the text the same transaction is writing.
 *
 * The edit is read as ONE replaced middle (`textSplice` — common prefix +
 * common suffix), which is exactly how `spliceYText` reads a value write, so
 * the two agree about what changed. Offsets after the splice shift by the length delta;
 * a range that STRADDLES the splice grows or shrinks with it; a range wholly
 * inside a deletion disappears. Anything this cannot place is left to the
 * quote search in `resolveTextMark`.
 */
export function rebaseTextMarks(before, after, marks) {
    if (before === after)
        return spansToMarks(after, resolveTextMarks(after, marks));
    const { cutStart, cutEnd, delta } = textSplice(before, after);
    const moved = [];
    for (const span of resolveTextMarks(before, marks)) {
        const start = shiftOffset(span.start, cutStart, cutEnd, delta);
        const end = shiftOffset(span.end, cutStart, cutEnd, delta);
        if (end <= start)
            continue;
        moved.push(span.color === undefined ? { start, end } : { start, end, color: span.color });
    }
    return spansToMarks(after, normalizeSpans(moved));
}
/**
 * One offset through the splice. Inside the replaced middle it collapses to
 * the splice start — so a mark that survives only by its edges keeps its
 * edges, and one that lived entirely inside the replaced text collapses to
 * an empty range and is dropped by the caller.
 */
function shiftOffset(offset, cutStart, cutEnd, delta) {
    if (offset <= cutStart)
        return offset;
    if (offset >= cutEnd)
        return offset + delta;
    return cutStart;
}
/* ------------------------------------------------------------------ */
/* Segmentation (what renderers draw)                                  */
/* ------------------------------------------------------------------ */
/**
 * Split `text` into runs of identical formatting, in order, covering it
 * exactly once. A cell with no marks and no kinds returns a single
 * unformatted segment — the hot path, since almost every cell on a board
 * carries neither.
 *
 * Boundaries come from every kind at once — the four mark kinds AND the
 * F-17-type line kinds (`types`, the cell's switch points) — so one span of
 * bold overlapping two highlights emits three segments and no nesting: the
 * script block's mirror layer must stay a flat, character-for-character match
 * of the textarea underneath it.
 */
export function segmentMarkedText(text, marks, types) {
    if (text.length === 0)
        return [];
    const bold = marks?.bold === undefined ? [] : resolveTextMarks(text, marks.bold);
    const strike = marks?.strike === undefined ? [] : resolveTextMarks(text, marks.strike);
    const color = marks?.color === undefined ? [] : resolveTextMarks(text, marks.color);
    const hl = marks?.hl === undefined ? [] : resolveTextMarks(text, marks.hl);
    const runs = types === undefined || types.length === 0 ? [] : textTypeRuns(text, types);
    if (bold.length === 0 &&
        strike.length === 0 &&
        color.length === 0 &&
        hl.length === 0 &&
        runs.length === 0) {
        return [{ text }];
    }
    const cuts = new Set([0, text.length]);
    for (const spans of [bold, strike, color, hl, runs]) {
        for (const span of spans) {
            if (span.start > 0 && span.start < text.length)
                cuts.add(span.start);
            if (span.end > 0 && span.end < text.length)
                cuts.add(span.end);
        }
    }
    const edges = [...cuts].sort((a, b) => a - b);
    const segments = [];
    for (let i = 0; i < edges.length - 1; i++) {
        const start = edges[i];
        const end = edges[i + 1];
        const segment = { text: text.slice(start, end) };
        if (coveredBy(bold, start))
            segment.bold = true;
        if (coveredBy(strike, start))
            segment.strike = true;
        const ink = colorAt(color, start);
        if (ink !== undefined)
            segment.color = ink;
        const back = colorAt(hl, start);
        if (back !== undefined)
            segment.hl = back;
        const kind = typeAt(runs, start);
        if (kind !== undefined)
            segment.type = kind;
        segments.push(segment);
    }
    return segments;
}
/** The line kind of the run covering `offset`, if any. */
function typeAt(runs, offset) {
    for (const run of runs) {
        if (run.start > offset)
            return undefined;
        if (offset < run.end)
            return run.ty;
    }
    return undefined;
}
/** Is `offset` inside one of these (sorted, non-overlapping) spans? */
function coveredBy(spans, offset) {
    for (const span of spans) {
        if (span.start > offset)
            return false;
        if (offset < span.end)
            return true;
    }
    return false;
}
/** The color of the span covering `offset`, if any. */
function colorAt(spans, offset) {
    for (const span of spans) {
        if (span.start > offset)
            return undefined;
        if (offset < span.end)
            return span.color;
    }
    return undefined;
}
//# sourceMappingURL=text-marks.js.map